import type { Feature, FeatureCollection } from 'geojson'

import { LineStyle } from '@/domain/protocols/UserGeometry'

import {
  computeGeoJsonBounds,
  GeoJsonBounds,
  splitGeoJsonForMapbox
} from '@/data/UserGeometryRepository/userGeometryGeoJsonRender'

import {
  isHiddenFeature,
  resolveFillOpacity,
  resolveLabelMinZoom,
  resolveLabelText,
  resolveLineStyle,
  resolveLineWidth,
  resolvePointColor,
  resolvePointRadius,
  resolvePointStrokeColor,
  resolveShapeColor,
  resolveStrokeOpacity,
  shouldShowLabel
} from './userGeometryRenderResolvers'

import { pinImageName } from '@/presentation/components/ColorCarousel'

// Derived (render-only) property keys. Resolved once here so the Mapbox paint
// expressions stay simple `['get', '__x']` lookups. Mapbox cannot compute
// luminance, color fallbacks or clamps, so they are baked into the features.
export const RENDER_COLOR_KEY = '__color'
export const RENDER_STROKE_COLOR_KEY = '__strokeColor'
export const RENDER_POINT_RADIUS_KEY = '__pointRadius'
export const RENDER_STROKE_OPACITY_KEY = '__strokeOpacity'
export const RENDER_FILL_OPACITY_KEY = '__fillOpacity'
export const RENDER_LINE_WIDTH_KEY = '__lineWidth'
export const RENDER_LABEL_KEY = '__label'
export const RENDER_HAS_LABEL_KEY = '__hasLabel'
export const RENDER_LABEL_MIN_ZOOM_KEY = '__labelMinZoom'
// Resolved line-style (defaults to 'solid') baked onto polygon features so the
// outline layers can filter by it — Mapbox cannot data-drive `line-dasharray`.
export const RENDER_LINE_STYLE_KEY = '__lineStyle'
// Mapbox image name for a point's pin icon (tinted by the resolved colour).
export const RENDER_POINT_ICON_KEY = '__pointIcon'

export interface UserGeometryLineCollections {
  solid: FeatureCollection
  dashed: FeatureCollection
  dotted: FeatureCollection
}

export interface UserGeometryRenderData {
  points: FeatureCollection
  lines: UserGeometryLineCollections
  polygons: FeatureCollection
  bounds: GeoJsonBounds | null
}

const EMPTY_COLLECTION = (): FeatureCollection => ({ type: 'FeatureCollection', features: [] })

const withRenderProperties = (feature: Feature, renderProps: Record<string, unknown>): Feature => ({
  ...feature,
  properties: {
    ...(feature.properties ?? {}),
    ...renderProps
  }
})

const buildPointFeature = (feature: Feature): Feature => {
  const properties = feature.properties ?? {}
  const color = resolvePointColor(properties)
  const label = resolveLabelText(properties)

  return withRenderProperties(feature, {
    [RENDER_COLOR_KEY]: color,
    [RENDER_POINT_ICON_KEY]: pinImageName(color),
    [RENDER_STROKE_COLOR_KEY]: resolvePointStrokeColor(color),
    [RENDER_POINT_RADIUS_KEY]: resolvePointRadius(properties),
    [RENDER_STROKE_OPACITY_KEY]: resolveStrokeOpacity(properties),
    [RENDER_LABEL_KEY]: label,
    [RENDER_HAS_LABEL_KEY]: shouldShowLabel(properties) && label !== '',
    [RENDER_LABEL_MIN_ZOOM_KEY]: resolveLabelMinZoom(properties, true)
  })
}

const buildLineFeature = (feature: Feature): Feature => {
  const properties = feature.properties ?? {}

  return withRenderProperties(feature, {
    [RENDER_COLOR_KEY]: resolveShapeColor(properties),
    [RENDER_LINE_WIDTH_KEY]: resolveLineWidth(properties),
    [RENDER_STROKE_OPACITY_KEY]: resolveStrokeOpacity(properties)
  })
}

const buildPolygonFeature = (feature: Feature): Feature => {
  const properties = feature.properties ?? {}
  const label = resolveLabelText(properties)

  return withRenderProperties(feature, {
    [RENDER_COLOR_KEY]: resolveShapeColor(properties),
    [RENDER_LINE_WIDTH_KEY]: resolveLineWidth(properties),
    [RENDER_STROKE_OPACITY_KEY]: resolveStrokeOpacity(properties),
    [RENDER_FILL_OPACITY_KEY]: resolveFillOpacity(properties),
    [RENDER_LINE_STYLE_KEY]: resolveLineStyle(properties),
    [RENDER_LABEL_KEY]: label,
    [RENDER_HAS_LABEL_KEY]: shouldShowLabel(properties) && label !== '',
    [RENDER_LABEL_MIN_ZOOM_KEY]: resolveLabelMinZoom(properties, false)
  })
}

const collect = (features: Feature[]): FeatureCollection => ({ type: 'FeatureCollection', features })

// Transforms a deserialized (already-optimized) geometry FeatureCollection into
// render-ready Mapbox collections: split by geometry type, hidden features
// dropped, per-feature paint values resolved, and line features partitioned by
// line-style (dasharray cannot be data-driven, so each style needs its own
// layer).
export const buildUserGeometryRenderData = (collection: FeatureCollection): UserGeometryRenderData => {
  const split = splitGeoJsonForMapbox(collection)

  const points = split.points.features.filter((feature) => !isHiddenFeature(feature.properties)).map(buildPointFeature)

  const polygons = split.polygons.features
    .filter((feature) => !isHiddenFeature(feature.properties))
    .map(buildPolygonFeature)

  const linesByStyle: Record<LineStyle, Feature[]> = { solid: [], dashed: [], dotted: [] }
  split.lines.features
    .filter((feature) => !isHiddenFeature(feature.properties))
    .forEach((feature) => {
      const style = resolveLineStyle(feature.properties)
      linesByStyle[style].push(buildLineFeature(feature))
    })

  return {
    points: collect(points),
    lines: {
      solid: linesByStyle.solid.length > 0 ? collect(linesByStyle.solid) : EMPTY_COLLECTION(),
      dashed: linesByStyle.dashed.length > 0 ? collect(linesByStyle.dashed) : EMPTY_COLLECTION(),
      dotted: linesByStyle.dotted.length > 0 ? collect(linesByStyle.dotted) : EMPTY_COLLECTION()
    },
    polygons: collect(polygons),
    bounds: computeGeoJsonBounds(collection)
  }
}
